import { RenderPass } from "@/core/pipeline/render-pass"
import { copyToTextureArray } from "@/core/utils/texture-copy"
import { Mask, MaskBlendMode, MaskGroup } from "@/core/types"
import equal from "fast-deep-equal"
import { Texture } from "@/core/backends/webgl/texture"
import { Renderer } from "../renderer"
import { LocalAdjustmentsMaxCount } from "@/core/utils/adjustments-data"
import { mat4 } from "gl-matrix"
import { isObjectMask } from "@/core/utils/masks"

export const maskSize = 1800 // 1800x1800 square mask seems precise enough for our use cases
export const minMaskLayers = 8
const margin = 4

export class MaskArrayPass extends RenderPass {
	private cache: Record<number, Mask[]> = {}
	private lastPhotoId: string = ""
	private lastCropMatrix: mat4 = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
	private lastDistortionSetting: string = ""

	render() {
		const { renderer } = this
		const { gl } = renderer.backend

		const localAdjustments = renderer.photo.adjustments.localAdjustments
		const maskBuffers = renderer.maskBuffers

		const maskCount = Math.min(localAdjustments?.length ?? 0, LocalAdjustmentsMaxCount)

		// update mask array layer number when necessary (try to reduce the update frequency by using a margin)
		if (maskCount > renderer.maskArray.layers) {
			this.updateMaskArrayLayers(maskCount + margin / 2)
		} else if (maskCount + margin < renderer.maskArray.layers) {
			const layers = Math.max(maskCount + margin / 2, minMaskLayers)
			this.updateMaskArrayLayers(layers)
		}

		if (!localAdjustments || maskCount === 0) {
			return
		}

		if (
			this.lastPhotoId !== renderer.photo.id ||
			!equal(this.lastCropMatrix, renderer.cropMatrix)
		) {
			this.dispose()
			this.lastPhotoId = renderer.photo.id
			this.lastCropMatrix = renderer.cropMatrix.slice() as mat4
		}

		// Object mask need to be re-rendered when distortion changes
		let reRenderObjectMask = false
		const distortionSetting =
			renderer.perspectiveMatrix.toString() +
			renderer.photo.adjustments.lensDistortion +
			renderer.photo.adjustments.correctLensDistortion
		if (distortionSetting !== this.lastDistortionSetting) {
			this.lastDistortionSetting = distortionSetting
			reRenderObjectMask = true
		}

		maskBuffers.updateSize(maskSize, maskSize)

		for (let i = 0; i < maskCount; i++) {
			const maskGroup = localAdjustments[i] as MaskGroup

			// skip if the i-th mask didn't change at all
			if (maskGroup.active && equal(this.cache[i], maskGroup.masks)) {
				if (includesObjectMask(maskGroup.masks) && reRenderObjectMask) {
					delete this.cache[i]
				} else {
					continue
				}
			}

			// clear mask buffer
			renderer.setRenderTarget(maskBuffers.readBuffer)
			gl.clearColor(0, 0, 0, 0)
			gl.clear(gl.COLOR_BUFFER_BIT)

			// add each mask of a maskGroup to the mask buffer
			let allMasksRendered = true
			if (maskGroup.active) {
				for (const mask of maskGroup.masks) {
					const maskRendered = renderMask(renderer, {
						mask,
						input: maskBuffers.readBuffer,
						output: maskBuffers.writeBuffer,
						channelIndex: 0,
					})
					allMasksRendered = allMasksRendered && maskRendered
				}
			} else {
				// if the mask group is not active, we need to clear the mask buffer
				allMasksRendered = false
			}

			// set the cache only when all masks are rendered (in case AI masks not ready)
			if (allMasksRendered) this.cache[i] = structuredClone(maskGroup.masks)
			else delete this.cache[i]

			// copy to maskArray with layerIndex=i
			copyToTextureArray({
				renderer,
				source: maskBuffers.readBuffer,
				destination: renderer.maskArray,
				layerIndex: i,
			})
		}
	}

	updateMaskArrayLayers(layers: number) {
		const { renderer } = this

		if (layers === renderer.maskArray.layers) return

		renderer.maskArray.delete()
		renderer.maskArray = renderer.backend.createTextureArray({
			label: "renderer.maskTextureArray",
			width: maskSize,
			height: maskSize,
			layers,
			format: "r8unorm",
		})

		this.dispose()
	}

	dispose() {
		this.cache = {}
	}
}

function includesObjectMask(masks: Mask[]) {
	for (const mask of masks) {
		if (isObjectMask(mask)) {
			return true
		}
	}
	return false
}

function getMaskTexture(renderer: Renderer, mask: Mask) {
	switch (mask.type) {
		case "subject":
		case "background":
		case "people":
		case "sky": {
			const texture = renderer.assets.getArtefact({ type: "ai-mask", id: mask.id })
			if (texture instanceof Texture) {
				return texture
			}
			return
		}
		case "brush": {
			return renderer.brushMaskPass.getMaskTexture(mask)
		}
		default: {
			return
		}
	}
}

export function renderMask(
	renderer: Renderer,
	options: {
		mask: Mask
		input: Texture
		output: Texture
		channelIndex?: number
		blendModeOverride?: MaskBlendMode
	},
) {
	const { mask, input, output, blendModeOverride } = options
	const channelIndex = options.channelIndex ?? 3

	if (!mask.active) {
		return true
	}

	const shaders = renderer.shaders
	const imageState = renderer.photo.imageState
	const cropMatrix = renderer.cropMatrix
	const perspectiveMatrix = renderer.perspectiveMatrix
	const blendMode = blendModeOverride ?? mask.blendMode

	const maskTexture = getMaskTexture(renderer, mask)

	renderer.setRenderTarget(output)
	input.bind(0)

	maskTexture?.bind(1)

	switch (mask.type) {
		case "gradient":
			shaders.gradientMask.render({
				inputTexture: 0,
				imageSize: imageState.originalImageSize,
				cropMatrix: cropMatrix,
				inverted: mask.inverted,
				subtract: blendMode === "subtract",
				startPoint: [mask.startX, mask.startY],
				endPoint: [mask.endX, mask.endY],
				channelIndex,
			})
			break

		case "radial":
			shaders.radialMask.render({
				inputTexture: 0,
				imageSize: imageState.originalImageSize,
				cropMatrix: cropMatrix,
				inverted: mask.inverted,
				subtract: blendMode === "subtract",
				center: [mask.centerX, mask.centerY],
				radius: [mask.radiusX, mask.radiusY],
				angle: mask.angle,
				feather: mask.feather,
				channelIndex,
			})
			break

		case "brush":
			if (!maskTexture) {
				return false
			}

			shaders.brushMask.setUniforms({
				inputTexture: 0,
				maskTexture: 1,
				cropMatrix: cropMatrix,
				perspectiveMatrix: perspectiveMatrix,
				inverted: mask.inverted,
				subtract: blendMode === "subtract",
				channelIndex,
			})

			shaders.brushMask.draw()
			break

		case "subject":
		case "background":
		case "people":
		case "sky":
			if (!maskTexture) {
				return false
			}

			renderer.photo.texture.bind(3)

			shaders.objectMask.setUniforms(renderer.profileManager.getLensUniforms(renderer.photo))
			shaders.objectMask.setUniforms({
				inputTexture: 0,
				maskTexture: 1,
				originalTexture: 3,
				cropMatrix: cropMatrix,
				perspectiveMatrix: perspectiveMatrix,
				inverted: mask.type === "background" ? !mask.inverted : mask.inverted,
				subtract: blendMode === "subtract",
				channelIndex,
			})
			shaders.objectMask.draw()
			break

		default:
			// Unknown mask type
			return true
	}

	input.swapWith(output)
	return true
}
